/**
 * useFeatureFlags — React hook for feature flag state
 * 
 * Fetches flags from GET /chat/features (or from the models endpoint)
 * and caches them in memory. Returns a simple key→boolean map.
 */

import { useState, useEffect } from 'react';
import { getApiUrl } from '../lib/env';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = getApiUrl();

// In-memory cache (shared across all hook instances)
let cachedFlags: Record<string, boolean> | null = null;
let fetchPromise: Promise<Record<string, boolean>> | null = null;

// Default fallback flags (everything enabled)
const FALLBACK_FLAGS: Record<string, boolean> = {
  web_search: true,
  extended_thinking: true,
  voice_mode: true,
  file_attachments: true,
};

async function fetchFlags(token?: string): Promise<Record<string, boolean>> {
  try {
    const res = await fetch(`${API_BASE}/chat/features`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return data.features || FALLBACK_FLAGS;
  } catch {
    return FALLBACK_FLAGS;
  }
}

/**
 * Hook: Returns resolved feature flags
 * 
 * Usage:
 *   const flags = useFeatureFlags();
 *   if (flags.web_search) { ... }
 */
export function useFeatureFlags(): Record<string, boolean> {
  const { user } = useAuth();
  const [flags, setFlags] = useState<Record<string, boolean>>(cachedFlags || FALLBACK_FLAGS);

  useEffect(() => {
    let cancelled = false;

    // If already cached, use it immediately
    if (cachedFlags) {
      setFlags(cachedFlags);
      return;
    }

    // Deduplicate concurrent fetches
    if (!fetchPromise) {
      fetchPromise = (async () => {
        const token = await user?.getIdToken?.();
        const result = await fetchFlags(token);
        cachedFlags = result;
        fetchPromise = null;
        return result;
      })();
    }

    fetchPromise.then(result => {
      if (!cancelled) setFlags(result);
    });

    return () => { cancelled = true; };
  }, [user]);

  return flags;
}

/**
 * Invalidate the cached flags (call after admin changes a flag)
 */
export function invalidateFeatureFlags(): void {
  cachedFlags = null;
  fetchPromise = null;
}
